// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { EagerLoadingLogoProvider } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import dismissRfqUpdates from "@insite/client-framework/Store/Context/Handlers/DismissRfqUpdates";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { AnyAction } from "@insite/client-framework/Store/Reducers";
import translate from "@insite/client-framework/Translate";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link from "@insite/mobius/Link";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngUtilities from "@steiner/App/Components/Common/XngUtilities";
import XngZone from "@steiner/App/Components/Common/XngZone";
import HeaderLayout from "@steiner/App/Components/Header/HeaderLayout";
import loadSystemList from "@steiner/App/Handlers/SystemList/SystemListHandler";
import useElementDimensions from "@steiner/App/Hooks/UseElementDimensions";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { setCSSVariable } from "@steiner/XDL/Utilities";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

export const HEADER_BREAKPOINT = "md";

const mapStateToProps = (state: CustomAppState) => ({
    siteName: state.context.website.name,
    session: getSession(state),
    myAccountPageUrl: getPageLinkByPageType(state, "MyAccountPage")?.url,
    // XNG-Changes
    isProductDetailsPage: getCurrentPage(state)?.type === "ProductDetailsPage",
    pdpMetaDataId: state.pages.productDetails.metadataSetForId,
});

const mapDispatchToProps = {
    dismissRfqUpdates,
    // XNG-Changes
    loadBoldedNavigation: () => (dispatch: Dispatch<AnyAction>) => {
        dispatch(loadSystemList({ systemListName: "BoldedNavigation" }) as any);
    },
};

type Props = PageProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;

export interface HeaderStyles {
    page?: MobiusPageProps;
    container?: InjectableCss;
    skipToContent?: SkipNavStyles;
    websiteName?: InjectableCss;
}

export const headerStyles: HeaderStyles = {
    page: {
        fullWidth: [true, true, false, false, false],
    },
    container: {
        css: css`
            background-color: ${getColor("secondary.main")};
            display: flex;
            > div:first-child {
                flex-grow: 1;
            }
            > div:last-child {
                max-width: 150px;
                display: flex;
                align-items: center;
                justify-content: center;
                height: auto;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                max-width: 100px;
                            `,
                        ],
                        "max",
                    )}
            }
        `,
    },
    websiteName: {
        css: css`
            @media print {
                display: block;
            }
            font-size: 12px;
            padding-left: 15px;
            display: none;
        `,
    },
};

const styles = headerStyles;

const Header = ({
    id,
    siteName,
    session,
    toaster,
    history,
    myAccountPageUrl,
    dismissRfqUpdates,
    loadBoldedNavigation,
    // XNG-Changes
    isProductDetailsPage,
    pdpMetaDataId,
}: Props) => {
    const [headerRef, size] = useElementDimensions();

    const viewMyDashboardClickHandler = () => {
        if (myAccountPageUrl) {
            history.push(myAccountPageUrl);
        }
    };

    // <XNG-Changes>
    const updateOgMetaDescription = () => {
        removeOgMetaDescription();

        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            const descriptionContent = metaDescription.getAttribute("content");
            const meta = document.createElement("meta");
            meta.setAttribute("property", "og:description");
            meta.setAttribute("content", descriptionContent || "");
            document.head.appendChild(meta);
        }
    };

    const removeOgMetaDescription = () => {
        document.querySelector('meta[property="og:description"]')?.remove();
    };

    useEffect(() => {
        if (!isProductDetailsPage) {
            removeOgMetaDescription();
            return;
        }
        updateOgMetaDescription();
    }, [isProductDetailsPage, pdpMetaDataId]);
    // </XNG-Changes>

    useEffect(() => {
        if (session.hasRfqUpdates && myAccountPageUrl) {
            toaster.addToast({
                body: (
                    <>
                        <Typography>{translate("You have new quote information.")}</Typography>
                        <Link onClick={viewMyDashboardClickHandler}>{translate("View My Dashboard")}</Link>
                    </>
                ),
                messageType: "info",
                timeoutLength: 1000 * 60 * 60 * 24 * 365,
                onClose: () => {
                    dismissRfqUpdates();
                },
            });
        }
    }, [myAccountPageUrl]);

    useEffect(() => {
        setCSSVariable("--header-height", `${size?.height || 112}px`);
    }, [size?.height]);

    useEffect(() => {
        loadBoldedNavigation();
    }, []);

    const afterHeader = React.createRef<HTMLSpanElement>();
    return (
        <>
            <StyledWrapper as="header" isHeader={true} {...styles.page} ref={headerRef}>
                <SkipNav
                    extendedStyles={styles.skipToContent}
                    text={translate("Skip to main content")}
                    destination={afterHeader}
                />
                <EagerLoadingLogoProvider value={true}>
                    <HeaderLayout id={id} />
                    <XngZone contentId={id} zoneName="Breadcrumb" />
                    <XngZone contentId={id} zoneName="Script" />
                </EagerLoadingLogoProvider>
                <span ref={afterHeader} tabIndex={-1} />
            </StyledWrapper>
            <XngUtilities />
        </>
    );
};

const pageModule: PageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(Header))),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export const HeaderContext = "HeaderContext";

export default pageModule;
